import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pageAction } from '../redux_files/actions/page_actions';
import { createOrder, clearOrderConfirmation, clearOrderError, clearOrderLoading } from '../redux_files/actions/order_actions';
import { showLoader, hideLoader } from '../redux_files/actions/loaderActions';
import { updateOrderFormField, setOrderFormError, setOrderFormStep, resetOrderForm, clearOrderFormError } from '../redux_files/actions/orderFormActions';
import OrderStep from '../components/order_step';
import DeliveryDateStep from '../components/order-steps/DeliveryDateStep';
import MealTypeStep from '../components/order-steps/MealTypeStep';
import PlateSizeStep from '../components/order-steps/PlateSizeStep';
import QuantityStep from '../components/order-steps/QuantityStep';
import DeliveryDetailsStep from '../components/order-steps/DeliveryDetailsStep';
import OrderSummaryStep from '../components/order-steps/OrderSummaryStep';
import { useMealAvailability } from '../hooks/useMealAvailability';
import { LUNCH_CUTOFF_MINUTES, DINNER_CUTOFF_MINUTES } from '../utils/constants';
import qrCodeImage from '../assets/QRCode.jpg';
import { getWhatsAppLink, PHONE_NUMBER } from '../utils/constants';
import { getDeviceTypeForOrderSource } from '../utils/common';
import OrderQRCode from '../components/OrderQRCode';

// Constants for delivery charge calculation
const FREE_DELIVERY_RADIUS_KM = 7;
const DELIVERY_CHARGE_PER_KM = 5;

const OrderFood = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderConfirmation: reduxOrderConfirmation, error: orderError, loading: orderLoading } = useSelector(state => state.order);
  const formData = useSelector(state => state.orderForm);
  const { currentStep, selectedDate, mealType, plateSize, numberOfPlates, deliveryDistance, selectedDeliveryCoords, name, phone, email, address, deliveryLocation, errors } = formData;
  
  // Local UI state only
  const [orderConfirmation, setOrderConfirmation] = React.useState(null);
  const [orderSubmissionError, setOrderSubmissionError] = React.useState('');
  const [redirectCountdown, setRedirectCountdown] = React.useState(60);
  const [retryCountdown, setRetryCountdown] = React.useState(60);
  const [userManuallyChangedMeal, setUserManuallyChangedMeal] = React.useState(false);
  const totalSteps = 6;

  // Check meal availability based on current time
  const mealAvailability = useMealAvailability(selectedDate);
  const stepNames = [
    'deliveryDate',
    'mealType',
    'plateSize',
    'numberOfPlates',
    'deliveryDetails',
    'orderSummary'
  ];

  // Continuously check and update meal selection based on availability
  useEffect(() => {
    // Only check meal availability on mealType step

    const currentStepName = stepNames[currentStep - 1];
    if (currentStepName !== 'mealType') return;

    // Don't auto-change meal if user manually changed it
    if (userManuallyChangedMeal) return;

    // If currently selected meal becomes unavailable, auto-switch or clear selection
    if (mealType === 'lunch' && !mealAvailability.lunchAvailable) {
      if (mealAvailability.dinnerAvailable) {
        dispatch(updateOrderFormField('mealType', 'dinner'));
      } else {
        dispatch(updateOrderFormField('mealType', null));
      }
    } else if (mealType === 'dinner' && !mealAvailability.dinnerAvailable) {
      if (mealAvailability.lunchAvailable) {
        dispatch(updateOrderFormField('mealType', 'lunch'));
      } else {
        dispatch(updateOrderFormField('mealType', null));
      }
    }
  }, [mealAvailability.lunchAvailable, mealAvailability.dinnerAvailable, mealType, dispatch, currentStep, userManuallyChangedMeal]);

  useEffect(() => {
    document.title = 'Order Food';
    dispatch(pageAction('Order Food'));
    // CRITICAL: Clear any stale state from previous orders FIRST
    // This must happen synchronously before any renders
    dispatch(clearOrderConfirmation());
    dispatch(clearOrderError());
    dispatch(clearOrderLoading());
    
    // Clear local UI state
    setOrderConfirmation(null);
    setOrderSubmissionError('');
    setRedirectCountdown(60);
    setRetryCountdown(60);
    
    // Reset order form to initial state (step 1, cleared fields)
    dispatch(resetOrderForm());
    // Explicitly ensure we're at step 1 on any mount
    dispatch(setOrderFormStep(1));

    // Disable hardware back button
    window.history.pushState(null, null, window.location.pathname);

    const handlePopState = () => {
      // Prevent going back and push the same state again
      window.history.pushState(null, null, window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch]);

  // Auto-select available meal based on current time whenever selectedDate changes (only on step 1)
  useEffect(() => {
    if (!selectedDate || currentStep !== 1) return;
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = selectedDate === todayString;

    let lunchAvailable = true;
    let dinnerAvailable = true;

    if (isToday) {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const totalMinutes = hours * 60 + minutes;

      // Lunch disabled: After 12:30 PM
      const lunchDisabled = totalMinutes >= LUNCH_CUTOFF_MINUTES;

      // Dinner disabled: After 6:30 PM
      const dinnerDisabled = totalMinutes >= DINNER_CUTOFF_MINUTES;

      lunchAvailable = !lunchDisabled;
      dinnerAvailable = !dinnerDisabled;
    }

    if (!lunchAvailable && !dinnerAvailable) {
      dispatch(updateOrderFormField('mealType', null));
    } else if (!lunchAvailable && dinnerAvailable) {
      dispatch(updateOrderFormField('mealType', 'dinner'));
    } else {
      dispatch(updateOrderFormField('mealType', 'lunch'));
    }
  }, [selectedDate, dispatch, currentStep]);

  // Auto-select meal when entering step 2 based on availability (only run once when entering step 2)
  const hasEnteredStep2Ref = React.useRef(false);
  useEffect(() => {
    if (currentStep === 2 && selectedDate && !hasEnteredStep2Ref.current) {
      hasEnteredStep2Ref.current = true;
      if (!mealAvailability.lunchAvailable && mealAvailability.dinnerAvailable) {
        dispatch(updateOrderFormField('mealType', 'dinner'));
      } else if (mealAvailability.lunchAvailable && !mealAvailability.dinnerAvailable) {
        dispatch(updateOrderFormField('mealType', 'lunch'));
      } else if (mealAvailability.lunchAvailable && mealAvailability.dinnerAvailable && !mealType) {
        // Both available and no selection - default to lunch
        dispatch(updateOrderFormField('mealType', 'lunch'));
      }
    }
    // Reset the flag when leaving step 2
    if (currentStep !== 2) {
      hasEnteredStep2Ref.current = false;
    }
  }, [currentStep, selectedDate, mealAvailability.lunchAvailable, mealAvailability.dinnerAvailable, mealType, dispatch]);

  // If today is closed (after 5:30 PM) and no date selected, default to tomorrow
  useEffect(() => {
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const isAfterCutoff = minutesNow >= DINNER_CUTOFF_MINUTES;
    if (currentStep === 1 && isAfterCutoff && !selectedDate) {
      const tmr = new Date(Date.now() + 24 * 60 * 60 * 1000);
      const ymd = `${tmr.getFullYear()}-${String(tmr.getMonth() + 1).padStart(2, '0')}-${String(tmr.getDate()).padStart(2, '0')}`;
      dispatch(updateOrderFormField('selectedDate', ymd));
      dispatch(updateOrderFormField('mealType', 'lunch'));
      dispatch(clearOrderFormError('date'));
      dispatch(clearOrderFormError('meal'));
    }
  }, [currentStep, selectedDate, dispatch]);

  // Hide delivery location error banner once a location is selected
  useEffect(() => {
    const hasLocationText = !!(deliveryLocation && deliveryLocation.trim());
    const hasCoords = !!(selectedDeliveryCoords && selectedDeliveryCoords.lat != null && selectedDeliveryCoords.lng != null);
    if (hasLocationText || hasCoords) {
      dispatch(clearOrderFormError('deliveryLocation'));
      setOrderSubmissionError('');
    }
  }, [deliveryLocation, selectedDeliveryCoords, dispatch]);

  // Hide Name/Phone/Address banner once fields become valid
  useEffect(() => {
    const nameValid = !!(name && name.trim() && /^[a-zA-Z\s]+$/.test(name.trim()));
    const phoneValid = !!(phone && phone.trim() && /^[6-9]\d{9}$/.test(phone.trim()));
    const addressValid = !!(address && address.trim());

    if (nameValid) {
      dispatch(clearOrderFormError('name'));
      setOrderSubmissionError('');
    }
    if (phoneValid) {
      dispatch(clearOrderFormError('phone'));
      setOrderSubmissionError('');
    }
    if (addressValid) {
      dispatch(clearOrderFormError('address'));
      setOrderSubmissionError('');
    }
  }, [name, phone, address, dispatch]);

  // Calculate price based on plate type and quantity
  const calculatePrice = () => {
    const plates = numberOfPlates === '' ? 0 : numberOfPlates;
    let platePrice = 0;
    let deliveryCharge = 0;

    if (plateSize === 'half') {
      platePrice = 50;
    } else {
      // Full plate pricing
      if (plates > 15) {
        platePrice = 55;
      } else if (plates > 6) {
        platePrice = 60;
      } else {
        platePrice = 70;
      }
    }

    // Calculate delivery charge based on distance
    if (deliveryDistance !== null) {
      if (deliveryDistance <= FREE_DELIVERY_RADIUS_KM) {
        // Within 7km - free delivery
        deliveryCharge = 0;
      } else {
        // Outside 7km
        if (plates >= 2) {
          // 2 or more orders - free delivery
          deliveryCharge = 0;
        } else if (plates === 1) {
          // Single order - ₹5 per km for distance beyond 7km
          const chargeableDistance = deliveryDistance - FREE_DELIVERY_RADIUS_KM;
          deliveryCharge = Math.ceil(chargeableDistance) * DELIVERY_CHARGE_PER_KM;
        }
      }
    }

    const subtotal = platePrice * plates;
    const total = subtotal + deliveryCharge;

    return {
      platePrice,
      subtotal,
      deliveryCharge,
      total,
      distance: deliveryDistance
    };
  };

  const pricing = calculatePrice();

  const hasErrors = Object.values(errors || {}).some(Boolean);

  // Helper function to determine if Next button should be disabled
  const isNextButtonDisabled = () => {
   
    const currentStepName = stepNames[currentStep - 1];
    if (currentStepName === 'deliveryDate' && !selectedDate) return true;
    if (currentStepName === 'mealType' && !mealType) return true;
    if (currentStepName === 'numberOfPlates' && (numberOfPlates === '' || numberOfPlates === 0)) return true;
    if (currentStepName === 'deliveryDetails' && errors.currentLocation && errors.currentLocation.includes('outside Panipat')) return true;
    if (hasErrors) return true;
    return false;
  };



  // Build order data from current form state
  const buildOrderData = () => ({
    // Order Details
    orderDate: new Date().toISOString(),
    deliveryDate: selectedDate,
    mealType: mealType,
    plateType: plateSize === 'full' ? 'Full Plate' : 'Buffet',
    numberOfPlates: numberOfPlates,

    // Pricing
    platePrice: pricing.platePrice,
    subtotal: pricing.subtotal,
    deliveryCharge: pricing.deliveryCharge,
    totalAmount: pricing.total,

    // Customer Details
    customerName: name,
    customerPhone: phone,
    customerEmail: email,

    // Delivery Details
    deliveryAddress: address,
    deliveryLocation: deliveryLocation,
    deliveryCoordinates: {
      latitude: selectedDeliveryCoords?.lat || null,
      longitude: selectedDeliveryCoords?.lng || null
    },

    // Order Status
    status: 'pending',
    paymentStatus: 'pending',

    // Order Source
    orderSource: {
      deviceType: getDeviceTypeForOrderSource()
    }
  });

  const handleNext = () => {
    // Validate by step name
    const currentStepName = stepNames[currentStep - 1];
    if (currentStepName === 'mealType') {
      // Reset the manual change flag when moving to next step
      setUserManuallyChangedMeal(false);
      // Check if no meal is selected (both closed)
      if (!mealType) {
        dispatch(setOrderFormError('meal', 'All meals are closed for today. Please select a different date.'));
        return;
      }
      const isLunchSelected = mealType === 'lunch';
      const isDinnerSelected = mealType === 'dinner';
      if (isLunchSelected && !mealAvailability.lunchAvailable) {
        dispatch(setOrderFormError('meal', 'Sorry, Lunch is closed. Please select Dinner.'));
        return;
      }
      if (isDinnerSelected && !mealAvailability.dinnerAvailable) {
        dispatch(setOrderFormError('meal', 'Sorry, Dinner is closed. Please select Lunch.'));
        return;
      }
    }

    if (currentStepName === 'numberOfPlates' && (numberOfPlates === '' || numberOfPlates === 0)) {
      dispatch(setOrderFormError('plate', 'Please select at least 1 plate'));
      return;
    }

    if (currentStepName === 'deliveryDetails') {
      let hasError = false;
      
      if (!name.trim()) {
        dispatch(setOrderFormError('name', 'Name is required'));
        hasError = true;
      } else if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
        dispatch(setOrderFormError('name', 'Name should contain only letters and spaces'));
        hasError = true;
      }
      
      if (!phone.trim()) {
        dispatch(setOrderFormError('phone', 'Phone number is required'));
        hasError = true;
      } else if (!/^[6-9]\d{9}$/.test(phone.trim())) {
        dispatch(setOrderFormError('phone', 'Please enter a valid 10-digit phone number'));
        hasError = true;
      }
      
      if (!address.trim()) {
        dispatch(setOrderFormError('address', 'Address is required'));
        hasError = true;
      }
      
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        dispatch(setOrderFormError('email', 'Please enter a valid email address'));
        hasError = true;
      }
      
      if (!deliveryLocation.trim()) {
        dispatch(setOrderFormError('deliveryLocation', 'Delivery location is required'));
        hasError = true;
      }
      
      if (hasError) {
        return;
      }
    }
    
    if (currentStep < stepNames.length) {
      dispatch(setOrderFormStep(currentStep + 1));
    }
  };

  const handlePrevious = () => {
    const currentStepName = stepNames[currentStep - 1];
    if (currentStepName === 'orderSummary') {
      setOrderSubmissionError('');
      // Clear Redux order error when navigating back from orderSummary to allow user to fix their order
      dispatch(clearOrderError());
    }
    if (currentStep > 1) {
      // If going back to mealType step, allow auto-switching of meal again
      const prevStepName = stepNames[currentStep - 2];
      if (prevStepName === 'mealType') {
        setUserManuallyChangedMeal(false);
      }
      dispatch(setOrderFormStep(currentStep - 1));
    }
  };

  const handleOrderSubmit = async () => {
    if (numberOfPlates === '' || numberOfPlates === 0) {
      dispatch(setOrderFormError('plate', 'Please select at least 1 plate'));
      dispatch(setOrderFormStep(3));
      setOrderSubmissionError('Please select at least 1 plate');
      return;
    }

    // Validate Name
    if (!name.trim()) {
      dispatch(setOrderFormError('name', 'Name is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Name is required');
      return;
    } else if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      dispatch(setOrderFormError('name', 'Name should contain only letters and spaces'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Name should contain only letters and spaces');
      return;
    }

    // Validate Phone
    if (!phone.trim()) {
      dispatch(setOrderFormError('phone', 'Phone number is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Phone number is required');
      return;
    } else if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      dispatch(setOrderFormError('phone', 'Please enter a valid 10-digit phone number'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate Address
    if (!address.trim()) {
      dispatch(setOrderFormError('address', 'Address is required'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Address is required');
      return;
    }

    // Require a delivery location selection before confirming
    const hasCoords = !!(selectedDeliveryCoords && selectedDeliveryCoords.lat != null && selectedDeliveryCoords.lng != null);
    if (!deliveryLocation || !deliveryLocation.trim() || !hasCoords) {
      dispatch(setOrderFormError('deliveryLocation', 'Please select a delivery location'));
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Please select a delivery location');
      return;
    }

    // Check if location is outside Panipat delivery area
    if (errors.currentLocation && errors.currentLocation.includes('outside Panipat')) {
      dispatch(setOrderFormStep(5));
      setOrderSubmissionError('Delivery is not available outside Panipat. Please select a location within Panipat.');
      return;
    }

    // Prepare order data
    const orderData = buildOrderData();

    // Show global activity indicator while submitting
    dispatch(showLoader());
    
    // Dispatch Redux action instead of direct fetch
    // Move to Order Summary screen while submitting
    dispatch(setOrderFormStep(6));
    dispatch(createOrder(orderData));
    // Clear any prior submission banner when submitting
    setOrderSubmissionError('');
  };

  // Allow retry after 1 minute on Order Summary when there is an error
  useEffect(() => {
    if (stepNames[currentStep - 1] === 'orderSummary' && orderSubmissionError && !orderConfirmation) {
      if (retryCountdown === 0) return;
      const timer = setTimeout(() => setRetryCountdown(retryCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Reset countdown when leaving summary or error clears
      if (retryCountdown !== 60) setRetryCountdown(60);
    }
  }, [currentStep, orderSubmissionError, orderConfirmation, retryCountdown]);

  const handleRetryOrder = () => {
    if (retryCountdown > 0) return; // Guard
    const orderData = buildOrderData();
    setOrderSubmissionError('');
    dispatch(showLoader());
    dispatch(createOrder(orderData));
  };

  // API timeout - if no response after 30 seconds, show error
  useEffect(() => {
    if (!orderLoading) return;

    const timeoutId = setTimeout(() => {
      // If still loading after 30 seconds, set error
      setOrderSubmissionError('Failed to submit order. Please try again.');
      dispatch(setOrderFormStep(6));
    }, 3000); // 3 seconds

    return () => clearTimeout(timeoutId);
  }, [orderLoading, dispatch]);

  // Listen for order success/failure from Redux
  // IMPORTANT: Only depend on reduxOrderConfirmation and orderError, NOT on form fields
  // This prevents the effect from re-running when form resets, which could cause a race condition
  useEffect(() => {
    if (reduxOrderConfirmation) {
      // Order successful - hide loader and show confirmation
      dispatch(hideLoader());
      setOrderConfirmation({
        orderId: reduxOrderConfirmation.orderId || 'N/A',
        mealLabel: `${numberOfPlates} ${plateSize === 'full' ? 'Full Plate' : 'Buffet'} (${mealType ? mealType.charAt(0).toUpperCase() + mealType.slice(1) : 'N/A'})`,
        total: pricing.total
      });
      setOrderSubmissionError('');
      setRedirectCountdown(60); // Reset countdown to 60 seconds
    }
    
    if (orderError) {
      // Order failed - hide loader and show error
      dispatch(hideLoader());
      setOrderSubmissionError(orderError);
      // Clear any previous order confirmation so QR code doesn't show
      setOrderConfirmation(null);
      // Only force navigation to step 6 if we're at step 5 (during submission) or already at step 6
      // Don't force navigation if user went back to earlier steps to fix their order
      if (currentStep >= 5) {
        dispatch(setOrderFormStep(6));
      }
    }
  }, [reduxOrderConfirmation, orderError, dispatch, numberOfPlates, plateSize, mealType, pricing.total, currentStep]);

  // Auto-redirect to home page after 1 minute with countdown (only on success)
  useEffect(() => {
    // Only redirect if order was confirmed AND no error
    if (!orderConfirmation || orderSubmissionError) return;

    if (redirectCountdown === 0) {
      // Clear order confirmation and local state before navigating
      setOrderConfirmation(null);
      setOrderSubmissionError('');
      dispatch(clearOrderConfirmation());
      navigate('/');
      return;
    }

    const timer = setTimeout(() => {
      setRedirectCountdown(redirectCountdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [orderConfirmation, orderSubmissionError, redirectCountdown, navigate, dispatch]);

  const handleStartNewOrder = () => {
    setOrderConfirmation(null);
    setOrderSubmissionError('');
    // Clear any existing order confirmation in Redux and reset timers
    dispatch(clearOrderConfirmation());
    setRedirectCountdown(60);
    setRetryCountdown(60);
    dispatch(resetOrderForm());
    dispatch(setOrderFormStep(1));
  };

  // Only show success screen if orderConfirmation is set AND there's no error AND we're not just landing on the page
  // The redirectCountdown > 0 ensures we're in the success state (redirectCountdown is only reset to 60 after successful order)
  if (orderConfirmation && !orderSubmissionError && redirectCountdown < 60) {
    return (
      <div className='container'>
        <OrderQRCode redirectCountdown={redirectCountdown} handleStartNewOrder={handleStartNewOrder} />
      </div>
    ); 
  }

  return (
    <>
      <div className='container'>
        <div className='order-food-wrapper'>
        
        {/* Progress Indicator */}
        <div className='progress-indicator'>
          <div 
            className='progress-steps'
            style={{ '--progress-percentage': `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          >
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div 
                key={step} 
                className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
              >
                <div className='step-number'>{step}</div>
              </div>
            ))}
          </div>
        </div>        
        {(() => {
          const stepComponents = {
            deliveryDate: <OrderStep title='Select Delivery Date'><DeliveryDateStep /></OrderStep>,
            mealType: <OrderStep title='Select Meal Type'><MealTypeStep /></OrderStep>,
            plateSize: <OrderStep title='Select Meal Plan'><PlateSizeStep /></OrderStep>,
            numberOfPlates: <OrderStep title='How Many Plates?'><QuantityStep /></OrderStep>,
            deliveryDetails: <OrderStep title='Delivery Details'><DeliveryDetailsStep /></OrderStep>,
            orderSummary: <OrderStep title='Order Summary'><OrderSummaryStep /></OrderStep>
          };
          const currentStepName = stepNames[currentStep - 1];
          return stepComponents[currentStepName] || null;
        })()}

        {/* Navigation Buttons */}
        {stepNames[currentStep - 1] === 'orderSummary' && orderSubmissionError && (
          <div className='order-submission-error'>
            <strong>⚠️ Error:</strong> {orderSubmissionError}
          </div>
        )}
          <div className='step-navigation mt-30'>
          <button 
            className={`nav-button prev-button ${currentStep === 1 ? 'disabled' : ''}`}
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            ← Previous
          </button>
          
          {currentStep < totalSteps ? (
            <button 
              className={`nav-button next-button ${isNextButtonDisabled() ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={isNextButtonDisabled()}
              style={{
                opacity: isNextButtonDisabled() ? 0.5 : 1,
                cursor: isNextButtonDisabled() ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          ) : (
            <button 
              className={`nav-button submit-button ${(orderLoading || (orderSubmissionError && retryCountdown > 0)) ? 'disabled' : ''}`}
              onClick={handleOrderSubmit}
              disabled={orderLoading || (orderSubmissionError && retryCountdown > 0)}
            >
              Confirm Order
            </button>
          )}
          </div>
           {stepNames[currentStep - 1] === 'orderSummary' && !orderConfirmation && orderSubmissionError && (
            <div className='retry-container'>
              <button
                className={`nav-button retry-button ${retryCountdown > 0 ? 'disabled' : ''}`}
                onClick={handleRetryOrder}
                disabled={retryCountdown > 0}
              >
                {retryCountdown > 0
                  ? `Retry in ${Math.floor(retryCountdown / 60)}:${String(retryCountdown % 60).padStart(2, '0')}`
                  : 'Retry'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderFood;
